<script setup lang="ts" generic="Row">
import { computed, ref, watch } from 'vue'
import UiIcon from '../UiIcon.vue'
import UiTableSettings from './UiTableSettings.vue'
import { isHidden, layoutFor, layoutOf, moveKey, orderedColumns, sameSort, sortRows, toggleSort, validSort } from './tableState'
import type { DataTableColumn, SortCriterion } from './types'
import { useTablePreferences, useTableSort } from './useTable'

// The table of every list in the apps. The person sorts by clicking a header (Shift + click adds a
// criterion), and hides/reorders columns in "Colunas e ordenação" (or by dragging a header).
// The layout is saved per person through the app's TablePreferencesStore; the sort stays in the session.
// sortMode "server": the rows come sorted from the API (the screen reads v-model:sort, see useTableSort).
const props = withDefaults(
  defineProps<{
    tableId: string
    label: string
    columns: DataTableColumn<Row>[]
    rows: readonly Row[]
    rowKey: keyof Row | ((row: Row) => string | number)
    sortMode?: 'client' | 'server'
    defaultSort?: SortCriterion[]
    rowClickable?: boolean
    rowClass?: (row: Row) => string | Record<string, boolean> | undefined
    minWidth?: string
    actionsLabel?: string
    // Raise it when the columns change meaning or presentation: layouts saved for another version are
    // ignored (the person sees the default and saves again).
    layoutVersion?: number
  }>(),
  { sortMode: 'client', defaultSort: () => [], rowClickable: false, rowClass: undefined, minWidth: '720px', actionsLabel: 'Ações', layoutVersion: 1 },
)

const emit = defineEmits<{ 'row-click': [row: Row] }>()
defineSlots<
  { [key: `cell-${string}`]: (props: { row: Row; value: unknown }) => unknown } & {
    actions?: (props: { row: Row }) => unknown
  }
>()

const sortModel = defineModel<SortCriterion[]>('sort')
const sessionSort = useTableSort(props.tableId, props.defaultSort)
const rawSort = computed<SortCriterion[]>({
  get: () => sortModel.value ?? sessionSort.value,
  set: (value) => {
    sessionSort.value = value
    sortModel.value = value
  },
})
// Only criteria of columns that exist and sort (the session may hold an older version of the screen).
const sort = computed<SortCriterion[]>({
  get: () => validSort(rawSort.value, props.columns),
  set: (value) => (rawSort.value = validSort(value, props.columns)),
})
// The cleaned sort replaces the stale one, so the screen's query (server mode) and the session follow.
watch(
  () => [rawSort.value, props.columns] as const,
  ([value]) => {
    const valid = validSort(value, props.columns)
    if (!sameSort(valid, value)) rawSort.value = valid
  },
  { immediate: true },
)

const store = useTablePreferences()
// The saved layout only counts for this version of the columns. Saved keys of columns that no longer
// exist are ignored; new columns enter at their default place (orderedColumns / isHidden).
const savedLayout = computed(() => store.layout(props.tableId))
const layout = computed(() => {
  const saved = layoutFor(savedLayout.value, props.layoutVersion)
  if (!saved) return undefined
  // A layout that would leave no column visible falls back to the default.
  const anyVisible = props.columns.some((column) => !isHidden(column, saved))
  return anyVisible ? saved : undefined
})
const allColumns = computed(() => orderedColumns(props.columns, layout.value))
const hidden = computed(() => new Set(allColumns.value.filter((column) => isHidden(column, layout.value)).map((column) => column.key)))
const visible = computed(() => allColumns.value.filter((column) => !hidden.value.has(column.key)))

const sortedRows = computed(() => (props.sortMode === 'server' ? [...props.rows] : sortRows(props.rows, sort.value, props.columns)))

function keyOf(row: Row): string | number {
  return typeof props.rowKey === 'function' ? props.rowKey(row) : (row[props.rowKey] as string | number)
}
function valueOf(column: DataTableColumn<Row>, row: Row): unknown {
  return column.format ? column.format(row) : (row as Record<string, unknown>)[column.key]
}
function textOf(column: DataTableColumn<Row>, row: Row): string {
  const value = valueOf(column, row)
  return value === null || value === undefined || value === '' ? '—' : String(value)
}

const rankOf = (key: string) => sort.value.findIndex((criterion) => criterion.key === key)
function ariaSort(column: DataTableColumn<Row>): 'ascending' | 'descending' | undefined {
  const criterion = sort.value[rankOf(column.key)]
  return criterion ? (criterion.desc ? 'descending' : 'ascending') : undefined
}
function sortTitle(column: DataTableColumn<Row>): string {
  const criterion = sort.value[rankOf(column.key)]
  const now = criterion ? `Ordenado ${criterion.desc ? 'do maior para o menor' : 'do menor para o maior'}. ` : ''
  return `${now}Clique para ordenar por ${column.label}; Shift + clique acrescenta à ordenação.`
}
function onHeaderClick(column: DataTableColumn<Row>, event: MouseEvent | KeyboardEvent) {
  if (column.sortable === false) return
  sort.value = toggleSort(sort.value, column.key, event.shiftKey)
}

function saveLayout(order: string[], hiddenKeys: string[]) {
  const set = new Set(hiddenKeys)
  const columns = order
    .map((key) => allColumns.value.find((column) => column.key === key))
    .filter((column): column is DataTableColumn<Row> => !!column)
  store.save(props.tableId, layoutOf(columns, set, props.layoutVersion, layout.value))
}
function reset() {
  store.save(props.tableId, null)
  sort.value = [...props.defaultSort]
}

// Dragging a header moves the column (same as the panel).
const draggingKey = ref<string | null>(null)
const overKey = ref<string | null>(null)
function onHeaderDrop(target: string) {
  const key = draggingKey.value
  draggingKey.value = overKey.value = null
  if (!key || key === target) return
  const order = allColumns.value.map((column) => column.key)
  saveLayout(moveKey(order, key, order.indexOf(target)), [...hidden.value])
}

const settingsOpen = ref(false)

function onRowClick(row: Row) {
  if (props.rowClickable) emit('row-click', row)
}
function onRowKeydown(row: Row, event: KeyboardEvent) {
  if (props.rowClickable && event.key === 'Enter' && event.target === event.currentTarget) emit('row-click', row)
}

function cellClasses(column: DataTableColumn<Row>) {
  return [
    column.cellClass,
    {
      'ui-table__cell--end': column.align === 'end' || column.numeric,
      'ui-table__cell--nowrap': column.nowrap || column.numeric,
      'ui-table__cell--strong': column.strong,
      'ui-table__cell--numeric': column.numeric,
    },
  ]
}
</script>

<template>
  <div class="ui-table-wrap">
    <table class="ui-table" :style="{ minWidth }" :aria-label="label">
      <thead>
        <tr>
          <th
            v-for="column in visible"
            :key="column.key"
            scope="col"
            class="ui-table__head"
            :class="[
              cellClasses(column),
              { 'ui-table__head--over': overKey === column.key && draggingKey !== column.key, 'ui-table__head--dragging': draggingKey === column.key },
            ]"
            :style="column.width ? { width: column.width } : undefined"
            :aria-sort="ariaSort(column)"
            draggable="true"
            @dragstart="draggingKey = column.key"
            @dragend="draggingKey = overKey = null"
            @dragover.prevent="overKey = column.key"
            @drop.prevent="onHeaderDrop(column.key)"
          >
            <button
              v-if="column.sortable !== false"
              type="button"
              class="ui-table__sort"
              :class="{ 'ui-table__sort--active': rankOf(column.key) >= 0 }"
              :title="sortTitle(column)"
              @click="onHeaderClick(column, $event)"
            >
              <span :class="{ 'ui-sr-only': column.srOnlyLabel }">{{ column.label }}</span>
              <span class="ui-table__indicator" aria-hidden="true">
                <UiIcon :name="rankOf(column.key) < 0 ? 'arrow-up-down' : sort[rankOf(column.key)]?.desc ? 'arrow-down' : 'arrow-up'" :size="13" :stroke-width="2.2" />
                <span v-if="rankOf(column.key) >= 0 && sort.length > 1" class="ui-table__rank">{{ rankOf(column.key) + 1 }}</span>
              </span>
            </button>
            <span v-else :class="{ 'ui-sr-only': column.srOnlyLabel }">{{ column.label }}</span>
          </th>
          <th scope="col" class="ui-table__head ui-table__tail">
            <span class="ui-sr-only">{{ actionsLabel }}</span>
            <button type="button" class="ui-table__settings" aria-label="Colunas e ordenação" title="Colunas e ordenação" @click="settingsOpen = true">
              <UiIcon name="columns" :size="16" />
            </button>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="row in sortedRows"
          :key="keyOf(row)"
          :class="[rowClass?.(row), { 'ui-table__row--clickable': rowClickable }]"
          :tabindex="rowClickable ? 0 : undefined"
          @click="onRowClick(row)"
          @keydown="onRowKeydown(row, $event)"
        >
          <td v-for="column in visible" :key="column.key" :class="cellClasses(column)" :data-label="column.label">
            <slot :name="`cell-${column.key}`" :row="row" :value="valueOf(column, row)">{{ textOf(column, row) }}</slot>
          </td>
          <td class="ui-table__tail" @click.stop @keydown.stop>
            <slot name="actions" :row="row" />
          </td>
        </tr>
      </tbody>
    </table>

    <UiTableSettings
      :open="settingsOpen"
      :columns="allColumns"
      :hidden="hidden"
      :sort="sort"
      @close="settingsOpen = false"
      @layout="saveLayout"
      @sort="(value) => (sort = value)"
      @reset="reset"
    />
  </div>
</template>

<style scoped>
/* position: relative keeps absolutely positioned children (the screen-reader labels) inside the scroller, so a
   wide table scrolls in its card instead of widening the page. */
.ui-table-wrap {
  position: relative;
  overflow-x: auto;
}
.ui-table {
  width: 100%;
  border-collapse: collapse;
}
.ui-table__head {
  text-align: left;
  padding: 12px 16px;
  font-size: var(--type-caption-1-size);
  letter-spacing: var(--type-overline-tracking);
  text-transform: uppercase;
  color: var(--tertiary-label);
  font-weight: var(--weight-semibold);
  border-bottom: var(--hairline) solid var(--separator-soft);
  white-space: nowrap;
}
.ui-table__head--over {
  box-shadow: inset 2px 0 0 var(--accent);
}
.ui-table__head--dragging {
  opacity: 0.5;
}
.ui-table__sort {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 4px;
  margin: -2px -4px;
  border: none;
  border-radius: var(--radius-sm);
  background: transparent;
  color: inherit;
  font: inherit;
  letter-spacing: inherit;
  text-transform: inherit;
  cursor: pointer;
}
.ui-table__sort:hover {
  color: var(--text-primary);
}
.ui-table__sort--active {
  color: var(--text-primary);
}
.ui-table__indicator {
  display: inline-flex;
  align-items: center;
  gap: 1px;
  color: var(--muted-2);
}
.ui-table__indicator :deep(svg) {
  width: 10px;
  height: 10px;
}
.ui-table__sort:hover .ui-table__indicator,
.ui-table__sort--active .ui-table__indicator {
  color: var(--text-primary);
}
.ui-table__rank {
  font-size: 10px;
  font-weight: var(--weight-bold);
  line-height: 1;
}
.ui-table td {
  padding: 12px 16px;
  border-bottom: var(--hairline) solid var(--separator-row);
  font-size: var(--type-body-size);
  vertical-align: middle;
}
.ui-table tbody tr:last-child td {
  border-bottom: none;
}
.ui-table tbody tr {
  transition: background var(--duration-fast) var(--ease-standard);
}
.ui-table tbody tr:hover {
  background: var(--surface-hover);
}
.ui-table__row--clickable {
  cursor: pointer;
}
.ui-table__tail {
  width: 1%;
  white-space: nowrap;
  text-align: right;
  padding-right: 12px;
}
.ui-table__settings {
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: var(--radius-md);
  background: transparent;
  color: var(--tertiary-label);
  cursor: pointer;
  vertical-align: middle;
}
.ui-table__settings :deep(svg) {
  width: 14px;
  height: 14px;
}
.ui-table__settings:hover {
  background: var(--surface-hover);
  color: var(--text-primary);
}
.ui-table__cell--end {
  text-align: right;
}
.ui-table__head.ui-table__cell--end .ui-table__sort {
  flex-direction: row-reverse;
}
.ui-table__cell--nowrap {
  white-space: nowrap;
}
.ui-table__cell--strong {
  font-weight: var(--weight-medium);
}
.ui-table__cell--numeric {
  font-variant-numeric: tabular-nums;
}
/* Below 1024px a row becomes a block: the first column as its title, the others as "label: value" lines. The
   header keeps only the columns button (sorting stays in "Colunas e ordenação"). */
@media (max-width: 1023px) {
  .ui-table-wrap {
    overflow-x: visible;
  }
  .ui-table {
    min-width: 0 !important;
  }
  .ui-table,
  .ui-table thead,
  .ui-table tbody,
  .ui-table tr,
  .ui-table td {
    display: block;
  }
  .ui-table thead tr {
    display: flex;
    justify-content: flex-end;
    border-bottom: var(--hairline) solid var(--separator-soft);
  }
  .ui-table thead th:not(.ui-table__tail) {
    display: none;
  }
  .ui-table thead .ui-table__tail {
    width: auto;
    padding: 6px 8px;
    border-bottom: none;
  }
  .ui-table tbody tr {
    position: relative;
    padding: 12px 16px;
    border-bottom: var(--hairline) solid var(--separator-row);
  }
  .ui-table tbody tr:last-child {
    border-bottom: none;
  }
  .ui-table td,
  .ui-table td.ui-table__cell--end {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
    padding: 2px 0;
    border: none;
    text-align: right;
    font-size: var(--type-footnote-size);
    color: var(--text-secondary);
    white-space: normal;
  }
  .ui-table td::before {
    content: attr(data-label);
    flex: none;
    text-align: left;
    font-size: var(--type-caption-1-size);
    letter-spacing: var(--type-overline-tracking);
    text-transform: uppercase;
    color: var(--tertiary-label);
  }
  .ui-table td:empty {
    display: none;
  }
  .ui-table td:first-child {
    display: block;
    padding: 0 40px 4px 0;
    text-align: left;
    font-size: var(--type-headline-size);
    font-weight: var(--weight-semibold);
    color: var(--text-primary);
  }
  .ui-table td:first-child::before,
  .ui-table td.ui-table__tail::before {
    content: none;
  }
  .ui-table td.ui-table__tail {
    position: absolute;
    top: 8px;
    right: 8px;
    width: auto;
    padding: 0;
  }
}
</style>
